import { useAuthStore, type SessionUser } from './auth-store';

/**
 * Roles the backend treats as write-capable. A user holding none of these is
 * effectively read-only, and the backend now rejects their write attempts on
 * the role-gated routes (events, follow-ups, …).
 *
 * This is intentionally a thin mirror of the server's checks for UX hardening —
 * it hides/disables actions that would 403 anyway. It is NOT an authorization
 * boundary; the backend remains the source of truth.
 */
const WRITE_ROLES = ['admin', 'sales', 'staff'];

export function hasRole(user: SessionUser | null, role: string): boolean {
  return user?.roles.includes(role) ?? false;
}

/** A user can perform writes if they hold any non-readonly (write-capable) role. */
export function canWrite(user: SessionUser | null): boolean {
  return user?.roles.some((r) => WRITE_ROLES.includes(r)) ?? false;
}

/** A user is read-only when they hold no write-capable role. */
export function isReadonly(user: SessionUser | null): boolean {
  return !canWrite(user);
}

export type Permissions = {
  user: SessionUser | null;
  /** Holds the `admin` role. Some actions (e.g. deletes) are admin-only. */
  isAdmin: boolean;
  /** Holds any write-capable role (create/edit/complete allowed). */
  canWrite: boolean;
  /** Holds no write-capable role — write actions should be hidden/disabled. */
  isReadonly: boolean;
};

/**
 * Lightweight role-aware permissions derived from the current session user.
 * Use this to hide or disable write actions for read-only users so the UI
 * matches what the backend will accept.
 */
export function usePermissions(): Permissions {
  const user = useAuthStore((s) => s.user);
  return {
    user,
    isAdmin: hasRole(user, 'admin'),
    canWrite: canWrite(user),
    isReadonly: isReadonly(user),
  };
}
